import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as reportService from '../../services/reportService';

export const fetchDashboard = createAsyncThunk(
  'reports/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const res = await reportService.getDashboard();
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch dashboard');
    }
  }
);

export const fetchDailyReport = createAsyncThunk(
  'reports/fetchDailyReport',
  async (date, { rejectWithValue }) => {
    try {
      const res = await reportService.getDailyReport(date);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch daily report');
    }
  }
);

export const fetchWeeklyReport = createAsyncThunk(
  'reports/fetchWeeklyReport',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getWeeklyReport(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch weekly report');
    }
  }
);

export const fetchMonthlyReport = createAsyncThunk(
  'reports/fetchMonthlyReport',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getMonthlyReport(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch monthly report');
    }
  }
);

export const fetchYearlyReport = createAsyncThunk(
  'reports/fetchYearlyReport',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getYearlyReport(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch yearly report');
    }
  }
);

export const fetchPaymentAnalytics = createAsyncThunk(
  'reports/fetchPaymentAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getPaymentAnalytics(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch payment analytics');
    }
  }
);

export const fetchStockAnalytics = createAsyncThunk(
  'reports/fetchStockAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getStockAnalytics(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch stock analytics');
    }
  }
);

export const fetchCustomerAnalytics = createAsyncThunk(
  'reports/fetchCustomerAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getCustomerAnalytics(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch customer analytics');
    }
  }
);

export const fetchProfitAnalytics = createAsyncThunk(
  'reports/fetchProfitAnalytics',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getProfitAnalytics(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch profit analytics');
    }
  }
);

export const fetchTopProducts = createAsyncThunk(
  'reports/fetchTopProducts',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getTopProducts(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch top products');
    }
  }
);

export const fetchTopCustomers = createAsyncThunk(
  'reports/fetchTopCustomers',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getTopCustomers(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch top customers');
    }
  }
);

export const fetchStaffPerformance = createAsyncThunk(
  'reports/fetchStaffPerformance',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.getStaffPerformance(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch staff performance');
    }
  }
);

export const generateCustomReport = createAsyncThunk(
  'reports/generateCustomReport',
  async (params, { rejectWithValue }) => {
    try {
      const res = await reportService.generateCustomReport(params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to generate custom report');
    }
  }
);

const initialState = {
  dashboard: null,
  dailyReport: null,
  weeklyReport: null,
  monthlyReport: null,
  yearlyReport: null,
  paymentAnalytics: null,
  stockAnalytics: null,
  customerAnalytics: null,
  profitAnalytics: null,
  customReport: null,
  topProducts: [],
  topCustomers: [],
  staffPerformance: [],
  loading: false,
  error: null,
};

const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReports: (state) => {
      state.dashboard = null;
      state.dailyReport = null;
      state.weeklyReport = null;
      state.monthlyReport = null;
      state.yearlyReport = null;
      state.paymentAnalytics = null;
      state.stockAnalytics = null;
      state.customerAnalytics = null;
      state.profitAnalytics = null;
      state.customReport = null;
      state.topProducts = [];
      state.topCustomers = [];
      state.staffPerformance = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload.dashboard || action.payload;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDailyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyReport = action.payload.report || action.payload;
      })
      .addCase(fetchDailyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWeeklyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeeklyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.weeklyReport = action.payload.report || action.payload;
      })
      .addCase(fetchWeeklyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.monthlyReport = action.payload.report || action.payload;
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchYearlyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchYearlyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.yearlyReport = action.payload.report || action.payload;
      })
      .addCase(fetchYearlyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPaymentAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentAnalytics = action.payload.analytics || action.payload;
      })
      .addCase(fetchPaymentAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStockAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.stockAnalytics = action.payload.analytics || action.payload;
      })
      .addCase(fetchStockAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.customerAnalytics = action.payload.analytics || action.payload;
      })
      .addCase(fetchCustomerAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProfitAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfitAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.profitAnalytics = action.payload.analytics || action.payload;
      })
      .addCase(fetchProfitAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTopProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.topProducts = action.payload.products || action.payload.data || action.payload;
      })
      .addCase(fetchTopProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTopCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.topCustomers = action.payload.customers || action.payload.data || action.payload;
      })
      .addCase(fetchTopCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStaffPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.staffPerformance = action.payload.staff || action.payload.data || action.payload;
      })
      .addCase(fetchStaffPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateCustomReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateCustomReport.fulfilled, (state, action) => {
        state.loading = false;
        state.customReport = action.payload.report || action.payload;
      })
      .addCase(generateCustomReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReports, clearError } = reportSlice.actions;
export default reportSlice.reducer;
